// src/modules/bkg/components/BKGAgent/FloatingChatWidget.tsx
//
// Floating chat widget that expands to show the BKG Agent chat panel.

import React, { useState, useEffect } from "react";
import { Box, Button, Sheet, Tooltip } from "@mui/joy";
import CloseIcon from "@mui/icons-material/Close";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import { ChatPanel } from "./ChatPanel";
import ChatErrorBoundary from "./ChatErrorBoundary";
import { useFeatureFlag } from "../../../../providers/FeatureFlagProvider";
import { useTutorialTarget } from "../../../../components/Tutorial";

const BKG_AGENT_SEEN_KEY = "bkg_agent_seen";

interface FloatingChatWidgetProps {
  workspaceId: string;
}

/**
 * Floating chat widget that appears in the lower-right corner.
 * Clicking the button expands/collapses the chat panel.
 * On first visit the button morphs into a pill ("✨ AI Assistant") for 3s to
 * signal the feature, then contracts back to the icon.
 */
export const FloatingChatWidget: React.FC<FloatingChatWidgetProps> = ({
  workspaceId,
}) => {
  const bkgAgentEnabled = useFeatureFlag("bkg_agent");
  const STORAGE_KEY = `bkg-chat-open-${workspaceId}`;
  const [isOpen, setIsOpen] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) === "true";
    } catch {
      return false;
    }
  });
  const [isPillExpanded, setIsPillExpanded] = useState(false);
  const tutorialTargetRef = useTutorialTarget("bkg-ai-assistant");

  // Persist open state to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, String(isOpen));
    } catch {
      /* ignore */
    }
  }, [isOpen, STORAGE_KEY]);

  // First-visit pill animation: expand for 3 seconds then contract.
  useEffect(() => {
    if (localStorage.getItem(BKG_AGENT_SEEN_KEY)) return;
    setIsPillExpanded(true);
    const timer = setTimeout(() => {
      setIsPillExpanded(false);
      localStorage.setItem(BKG_AGENT_SEEN_KEY, "1");
    }, 3000);
    return () => clearTimeout(timer);
  }, []);

  const toggleChat = () => {
    // Clicking while pill is expanded dismisses it immediately.
    if (isPillExpanded) {
      setIsPillExpanded(false);
      localStorage.setItem(BKG_AGENT_SEEN_KEY, "1");
    }
    setIsOpen((prev) => !prev);
  };

  // Show the pill text only when not yet opened by the user.
  const showPillText = isPillExpanded && !isOpen;

  if (!bkgAgentEnabled) return null;

  return (
    <>
      {/* Chat Panel */}
      {isOpen && (
        <Sheet
          variant="outlined"
          sx={{
            position: "fixed",
            bottom: 80,
            right: 24,
            width: 520,
            height: "min(700px, calc(100vh - 160px))",
            minHeight: 400,
            borderRadius: "lg",
            boxShadow: "lg",
            zIndex: 1300,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <ChatErrorBoundary>
            <ChatPanel workspaceId={workspaceId} />
          </ChatErrorBoundary>
        </Sheet>
      )}

      {/* Floating Button — morphs between circle and pill */}
      <Box
        ref={tutorialTargetRef}
        sx={{
          position: "fixed",
          bottom: 24,
          right: 24,
          zIndex: 1300,
        }}
      >
        <Tooltip
          title="BKG AI Assistant"
          placement="left"
          arrow
          disableHoverListener={showPillText}
        >
          <Button
            onClick={toggleChat}
            variant="solid"
            color={isOpen ? "neutral" : "primary"}
            sx={{
              height: 48,
              minWidth: 48,
              borderRadius: "24px",
              px: 0,
              boxShadow: "md",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              overflow: "hidden",
              transition: "all 0.2s ease-in-out",
              "&:hover": {
                transform: "scale(1.05)",
                boxShadow: "lg",
              },
            }}
          >
            {/* Icon — always visible, horizontally padded to centre in circle mode */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                flexShrink: 0,
                pl: 1.5,
                pr: showPillText ? 0.5 : 1.5,
                transition: "padding 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
              }}
            >
              {isOpen ? <CloseIcon /> : <AutoAwesomeIcon />}
            </Box>

            {/* Label — slides in/out via max-width transition */}
            <Box
              sx={{
                maxWidth: showPillText ? "140px" : 0,
                overflow: "hidden",
                whiteSpace: "nowrap",
                pr: showPillText ? 1.5 : 0,
                transition:
                  "max-width 0.4s cubic-bezier(0.4, 0, 0.2, 1), padding-right 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                fontSize: "0.875rem",
                fontWeight: 600,
              }}
            >
              AI Assistant
            </Box>
          </Button>
        </Tooltip>
      </Box>
    </>
  );
};

export default FloatingChatWidget;
